/* eslint-disable camelcase */
/* eslint-disable react/jsx-props-no-spreading */
import { Button, Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap";
import { camelCase, isArray } from "lodash";
import { Formik } from "formik";
import PropTypes from "prop-types";
import { useMemo } from "react";

import ACFCheckboxes from "./ACFCheckboxes";
import ACFInput from "./ACFInput";
import ACFOptInCheckbox from "./ACFOptInCheckbox";
import classNames from "classnames";
import { labelText } from "./acfHelpers";

const ACFFieldGroupForm = ({
	buttonText,
	id,
	fields,
	formValues,
	loading,
	omitFields,
	onSubmit,
	title,
	transformFields
}) => {

	const transformedFields = useMemo(() => {
		let filteredFields = fields;
		if (transformFields) {
			filteredFields = transformFields(fields);
		}
		return {
			...filteredFields,
			fields: filteredFields.fields.filter((field) => omitFields.indexOf(field.name) === -1)
		};
	}, [fields, transformFields, omitFields]);

	// eslint-disable-next-line react/prop-types
	const defaultValues = useMemo(() => isArray(fields?.fields) ? fields.fields.reduce(
		(prev, cur) => {
			if (cur.default_value) {
				prev[camelCase(cur.name)] = cur.default_value;
			}
			return prev;
		},
		{}
	) : {}, [fields]);

	return (
		<div className="acf-field-group-container">
			{title ? <h3>{title}</h3> : null}
			<Formik
				initialValues={{
					...formValues,
					...defaultValues
				}}
				onSubmit={onSubmit}
			>
				{({
					handleChange,
					handleSubmit,
					values
				}) => (
					<Form id={id} noValidate onSubmit={handleSubmit}>
						<Row>
							{transformedFields.fields.map(({
								allow_custom: allowOther,
								choices,
								default_value,
								instructions,
								label,
								key,
								message,
								name,
								required,
								type: inputType,
								wrapper
							}) => {
								const camelName = name.search("_") === -1 ? name.toLowerCase() : camelCase(name);

								return (
									<Col
										className={classNames("flex-fill", wrapper.class)}
										key={key}
										sm={12}
										style={{ width: `${wrapper?.width || 100}%` }}
										xs={12}
									>
										{inputType === "message"
											? <div className="acf-input acf-input-type-message" dangerouslySetInnerHTML={{ __html: message }} />
											: inputType === "checkbox"
												? (
													<ACFCheckboxes {
														...{
															allowOther,
															choices,
															className: "mb-4",
															instructions,
															label,
															name: camelName,
															onChange: handleChange,
															required: !!required,
															value: formValues[camelName]
														}}
													/>
												)
												: wrapper.id === "completed"
													? <Form.Control name={camelName} type="hidden" value={formValues[camelName]} />
													: inputType === "true_false"
														? (
															<ACFOptInCheckbox {
																...{
																	instructions,
																	label,
																	message,
																	name: camelName,
																	onChange: handleChange,
																	required,
																	value: values[camelName]
																}}
															/>)
														: (
															<FloatingLabel
																className={classNames("mb-4")}
																controlId={camelName}
																label={labelText(label, required)}
																required={required}
															>
																{/* eslint-disable-next-line object-property-newline */}
																<ACFInput {
																	...{
																		choices,
																		default_value,
																		name: camelName,
																		onChange: handleChange,
																		placeholder: labelText(label, required),
																		required: !!required,
																		type: inputType,
																		value: values[camelName]
																	}}
																/>
															</FloatingLabel>)}
									</Col>
								);
							})}
						</Row>
						<Button disabled={loading} type="submit">
							{loading ? <Spinner animation="border" size="sm" /> : buttonText}</Button>
					</Form>
				)}
			</Formik>
		</div>
	);
};

ACFFieldGroupForm.propTypes = {
	buttonText: PropTypes.string,
	fields: PropTypes.shape({}),
	formValues: PropTypes.shape({}),
	id: PropTypes.string.isRequired,
	loading: PropTypes.bool,
	omitFields: PropTypes.arrayOf(PropTypes.string),
	onSubmit: PropTypes.func,
	title: PropTypes.string,
	transformFields: PropTypes.func
};

ACFFieldGroupForm.defaultProps = {
	buttonText: "Submit",
	fields: {},
	formValues: {},
	loading: false,
	omitFields: [],
	onSubmit: () => null,
	title: null,
	transformFields: null
};

export default ACFFieldGroupForm;
